import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { errText } from "@/lib/api";
import LoaderOlimpo from "@/components/LoaderOlimpo";
import { tamanoValido, RANGO } from "@/lib/validacion";

const EMPRESA_Q = [
  { key: "nombre_empresa", campoEmpresa: "nombre", texto: "¿Cómo se llama tu empresa?", ph: "El nombre" },
  { key: "rubro", campoEmpresa: "rubro", texto: "¿A qué se dedica?", ph: "En una frase" },
  { key: "tamano", campoEmpresa: "tamano", texto: "¿Cuántas personas son?", ph: "Un número" },
];
const ROL_Q = { key: "rol", esRol: true, texto: "¿Cuál es tu rol en la empresa?", ph: "Por ejemplo: dueño, contadora, encargado de ventas…" };

export default function Conocerte() {
  const nav = useNavigate();
  const { user, setEmpresa, refreshMe } = useAuth();
  const [preguntas, setPreguntas] = useState([]);
  const [paso, setPaso] = useState(0);
  // Buffer de respuestas: NO se guarda nada hasta que estén TODAS listas (no se crea a medias).
  const [respuestas, setRespuestas] = useState({});
  const [valor, setValor] = useState("");
  const [listo, setListo] = useState(false);
  const [emp, setEmp] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [iniciado, setIniciado] = useState(false);

  useEffect(() => {
    (async () => {
      const data = await refreshMe();
      const e = data?.empresa;
      const u = data?.usuario;
      setEmp(e);
      const faltantesEmp = EMPRESA_Q.filter((q) => !((e?.[q.campoEmpresa] || "").trim()));
      const rolFalta = !((u?.rol_en_empresa || "").trim());
      const lista = [...faltantesEmp, ...(rolFalta ? [ROL_Q] : [])];
      if (lista.length === 0) { nav("/boot"); return; }
      setPreguntas(lista);
      setPaso(0);
      setValor("");
      setIniciado(true);
    })();
  }, []);

  const pregunta = preguntas[paso];
  const esUltima = paso >= preguntas.length - 1;
  const esPrimera = paso === 0;

  // Guarda TODO junto, recién cuando todas las respuestas están completas.
  const guardarTodo = async (todas) => {
    setCargando(true);
    try {
      let ultimaEmpresa = emp;
      for (const q of preguntas) {
        const v = (todas[q.key] || "").trim();
        if (!v) continue;
        if (q.esRol) {
          await api.post("/usuario/rol", { rol: v });
        } else {
          const { data } = await api.post("/empresa/conocerte-paso", { campo: q.key, valor: v });
          ultimaEmpresa = data.empresa;
          setEmp(data.empresa);
          setEmpresa(data.empresa);
        }
      }
      await refreshMe();
      setEmp(ultimaEmpresa);
      setListo(true);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setCargando(false);
    }
  };

  const siguiente = async () => {
    if (!valor.trim()) return;
    if (pregunta.key === "tamano" && !tamanoValido(valor)) {
      toast.error(RANGO.personas.msg);
      return;
    }
    const nuevas = { ...respuestas, [pregunta.key]: valor.trim() };
    setRespuestas(nuevas);
    if (!esUltima) {
      const sig = paso + 1;
      setPaso(sig);
      setValor(nuevas[preguntas[sig].key] || "");
    } else {
      await guardarTodo(nuevas);
    }
  };

  const volver = () => {
    if (esPrimera || cargando) return;
    // Conservo lo tipeado (aunque esté a medias) para no perderlo al volver.
    const nuevas = { ...respuestas, [pregunta.key]: valor };
    setRespuestas(nuevas);
    const prev = paso - 1;
    setPaso(prev);
    setValor(nuevas[preguntas[prev].key] || "");
  };

  if (!iniciado) return <LoaderOlimpo fullscreen />;

  if (listo) {
    return (
      <div className="min-h-screen bg-negro flex flex-col items-center justify-center px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <h1 className="titulo text-[42px] leading-tight mb-1">Listo, {user?.nombre}.</h1>
          <h1 className="titulo text-[42px] leading-tight mb-10">Ya estamos.</h1>
          <div className="text-gris text-lg mb-14">{[emp?.rubro, emp?.tamano ? `${emp.tamano} personas` : null, emp?.pais].filter(Boolean).join(" · ")}</div>
          <button className="btn-dorado" onClick={() => nav("/boot")} data-testid="empecemos">EMPECEMOS</button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-negro flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-xl">
        <div className="mono-label text-gris mb-12">PREGUNTA {paso + 1} DE {preguntas.length}</div>
        <AnimatePresence mode="wait">
          <motion.div key={paso} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}>
            <h1 className="titulo text-4xl md:text-5xl mb-10">{pregunta.texto}</h1>
            <input
              autoFocus
              className="input-dark text-lg"
              placeholder={pregunta.ph}
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") siguiente(); }}
              data-testid="conocerte-input"
            />
            <div className="mt-8 flex items-center gap-4">
              {!esPrimera && (
                <button
                  className="btn-fantasma"
                  onClick={volver}
                  disabled={cargando}
                  data-testid="conocerte-volver"
                >
                  VOLVER
                </button>
              )}
              <button className="btn-dorado" onClick={siguiente} disabled={cargando || !valor.trim()} data-testid="conocerte-siguiente">
                {!esUltima ? "SIGUIENTE" : cargando ? "LISTO…" : "TERMINAR"}
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
